// =============================================================================
// ui/share/SharedReport.tsx — the page a stranger sees at ?share=<token>.
// -----------------------------------------------------------------------------
// No session, no providers, no app chrome. It is mounted BEFORE the auth flow
// (see App.tsx) and reads one anonymous endpoint.
//
// ── 🚨 THE REPORT IS RENDERED IN A SANDBOXED IFRAME, NOT INTO THIS PAGE ──
//
// `payload.html` is attacker-controllable — see io/share/render.ts for the whole
// argument. The frame carries NEITHER `allow-scripts` NOR `allow-same-origin`,
// which makes the document inert and puts it in an opaque origin: it cannot run
// code, cannot reach this page, and cannot read storage or cookies.
//
// ⚠ ANY CHANGE THAT RENDERS THE PAYLOAD INTO THIS DOCUMENT RE-OPENS IT,
// including a "temporary" one to measure something. There is a measurement
// helper that does it safely; use that.
//
// ── 🚨 THE TRADES TABLE IS OURS, OUTSIDE THE FRAME ──
//
// It used to be captured as part of the report and therefore lived INSIDE the
// sandbox, where nothing can bind a click — the rows looked identical to the
// real thing and did nothing at all. The fix is not to weaken the sandbox: the
// table is DATA we already serialise, so this page draws the real table, fully
// clickable, while the report above it stays an inert picture.
// =============================================================================

import { useEffect, useMemo, useRef, useState } from 'react';
import {
  SharedReportUnavailable, fetchSharedReport,
  shareFrameHeightPx, type ShareKind, type SharePayload, type ShareTrade,
} from '../../selectors/share';
import { isPnlUnit } from '../../selectors/pnlUnit';
import { maskAccountDigits } from '../../selectors/share';
import {
  buildFrameSrcdoc, collectAppCss, measureSrcdocHeight, stripCapturedTable,
} from '../../io/share/render';
import { SharedTradeModal } from './SharedTradeModal';
import { TradeTable } from '../tabs/TradeTable';
import { sharedTableColumns, sharedTradeRows } from './sharedTradeRows';

type State =
  | { kind: 'loading' }
  | { kind: 'failed'; message: string }
  | { kind: 'ready'; kind2: ShareKind | null; payload: Partial<SharePayload> };

export function SharedReport({ token }: { token: string }) {
  const [state, setState] = useState<State>({ kind: 'loading' });
  const [openIndex, setOpenIndex] = useState<number | null>(null);
  const frameRef = useRef<HTMLIFrameElement | null>(null);
  const [frameHeight, setFrameHeight] = useState<number | null>(null);
  /**
   * The app's stylesheets, as SOURCE TEXT. Null until they are read.
   *
   * ⚠ ASYNCHRONOUS NOW, because in a built app the CSS is a <link> and reading
   * its source means fetching the file. The alternative — serialising the CSSOM
   * — is what put every shared report in Times New Roman. See io/share/render.ts.
   *
   * ⚠ Started in parallel with the payload fetch rather than after it: they do
   * not depend on each other, and a report that waits for two round trips in
   * series is a slower page for no reason.
   */
  const [css, setCss] = useState<string | null>(null);

  useEffect(() => {
    let alive = true;
    void collectAppCss().then((c) => { if (alive) setCss(c); });
    return () => { alive = false; };
  }, []);

  useEffect(() => {
    // ⚠ The tab title says what this is. A stranger opening a link should not
    // see the app's own title, which implies they are signed into something.
    document.title = 'Shared report · TRADECOACH';
    let alive = true;
    void (async () => {
      try {
        const { kind, payload } = await fetchSharedReport(token);
        if (alive) setState({ kind: 'ready', kind2: kind, payload });
      } catch (e) {
        if (!alive) return;
        setState({
          kind: 'failed',
          message: e instanceof SharedReportUnavailable
            ? e.message
            : 'This shared report could not be loaded.',
        });
      }
    })();
    return () => { alive = false; };
  }, [token]);

  const p = state.kind === 'ready' ? state.payload : null;
  const trades: ShareTrade[] = useMemo(
    () => (Array.isArray(p?.trades) ? p.trades : []),
    [p],
  );

  /**
   * The payload's trades, in the shape the app's own table renders.
   *
   * ⚠ The four lookups exist because `TradeTable` resolves an account, a
   * pattern and a value through FUNCTIONS — the signed-in app passes registries
   * and a contract catalogue, and a visitor has neither. Here every one of those
   * was resolved at share time, so the lookups just hand back what was stored.
   */
  const { rows, valueById, accountById, patternById, indexById } =
    useMemo(() => sharedTradeRows(trades), [trades]);

  /**
   * ⚠ THE SHARER'S UNIT, not a guess. A payload written before this field
   * existed has none, and dollars is what those links already showed.
   */
  const unit = isPnlUnit(p?.unit) ? p.unit : 'usd';

  /**
   * ⚠ THE COLUMNS THIS PAYLOAD CAN ACTUALLY FEED. A link made before a column
   * existed carries nothing for it, and printing `$0.00` there is a figure this
   * report never had. See sharedTradeRows.ts.
   */
  const layout = useMemo(() => sharedTableColumns(trades), [trades]);

  /** The document the frame shows: the payload, edited inertly, plus our CSS. */
  const srcdoc = useMemo(() => {
    // ⚠ BOTH halves, or nothing. Building the frame before the CSS lands would
    // paint the report unstyled and then restyle it — a flash of exactly the
    // "broken page" this whole file exists to avoid.
    if (!p || css === null) return null;
    const html = stripCapturedTable(
      typeof p.html === 'string' && p.html ? p.html : '<div class="muted">Empty report.</div>',
      trades.length > 0,
    );
    return buildFrameSrcdoc({
      css,
      kind: state.kind === 'ready' ? state.kind2 : null,
      html,
    });
  }, [p, css, trades.length, state]);

  /**
   * ⚠ MEASURED, then applied — and the STORED height is only a fallback.
   *
   * The capture records the height of the stripped clone, which stops being
   * right the moment this renderer removes anything more (it does). A number
   * frozen in the database cannot describe HTML the renderer edits.
   */
  useEffect(() => {
    if (!srcdoc) return;
    let alive = true;
    void (async () => {
      const width = frameRef.current?.clientWidth || 1480;
      const measured = await measureSrcdocHeight(srcdoc, width);
      if (alive) setFrameHeight(shareFrameHeightPx(measured, p?.contentHeight ?? null));
    })();
    return () => { alive = false; };
  }, [srcdoc, p]);

  if (state.kind === 'loading') {
    return <div className="sr-page"><p className="sr-loading muted">Loading shared report…</p></div>;
  }

  if (state.kind === 'failed') {
    return (
      <div className="sr-page">
        <div className="sr-unavailable" role="alert">
          <h2>Report unavailable</h2>
          <p className="muted">{state.message}</p>
        </div>
      </div>
    );
  }

  const title = typeof p?.title === 'string' && p.title ? p.title : 'Report';
  const subtitle = typeof p?.subtitle === 'string' ? p.subtitle : '';
  /**
    * 🔴 MASKED ON THE WAY OUT AS WELL AS ON THE WAY IN, AND THE SECOND HALF IS
    * WHAT MAKES OLD LINKS HEAL.
    *
    * Masking only at capture time fixes links made from then on and leaves every
    * link ALREADY SENT carrying the account name in full, above the fold,
    * forever — with revocation the only remedy. Josh hit exactly that an hour
    * after the capture-side fix: "The account number is still not obfuscated at
    * the top header."
    *
    * ⚠ This is beta's own rule for this feature, which its renderer states
    * outright about the captured trades table: the same removal runs on the way
    * OUT so that old links heal, new links are belt-and-braces, and there is no
    * version of a payload that produces the wrong thing. It applies just as
    * plainly to a name as to a table, and it was applied to one and not the
    * other.
    *
    * ⚠ Masking twice is idempotent — the asterisks it writes are not digits.
    */
   const accountLabel = maskAccountDigits(
     typeof p?.accountLabel === 'string' ? p.accountLabel : '',
   );

  return (
    <div className="sr-page">
      <header className="sr-head">
        <div>
          <div className="sr-eyebrow">Shared report · read-only</div>
          <h1 className="sr-title">{title}{subtitle ? ` — ${subtitle}` : ''}</h1>
          {accountLabel ? <div className="muted sr-account">{accountLabel}</div> : null}
        </div>
        <div className="muted sr-brand">via <b>TRADECOACH</b></div>
      </header>

      {/* ⚠ THE EMPTY `sandbox` ATTRIBUTE IS EVERY RESTRICTION ON. Adding a token
          here re-opens what this closes; `allow-same-origin` in particular would
          undo all of it. See io/share/render.ts. */}
      <iframe
        ref={frameRef}
        className="sr-frame"
        sandbox=""
        referrerPolicy="no-referrer"
        title="Shared report"
        srcDoc={srcdoc ?? ''}
        /* ⚠ Sized from the measurement so the whole PAGE scrolls as one. A frame
            left at a guess scrolls internally — a scrollbar inside a scrollbar,
            with the report clipped mid-table. */
        style={frameHeight ? { height: `${frameHeight}px` } : { height: 'calc(100vh - 96px)' }}
      />

      {trades.length ? (
        <section className="sr-trades">
          <div className="an-section-divider">
            <span className="an-label">Trades</span><span className="an-rule" />
          </div>
          <div className="card">
            <p className="muted sr-trades-note">
              {trades.length} trade{trades.length === 1 ? '' : 's'} · click any row
              to open the review. Read-only.
            </p>
            {/* ⚠ SAID OUT LOUD rather than left as a narrower table nobody can
                explain. The alternative was a column of $0.00 that this report
                never contained. */}
            {layout.missing.length ? (
              <p className="muted sr-trades-note" role="status">
                {layout.missing.join(' and ')}{' '}
                {layout.missing.length === 1 ? 'is' : 'are'} not in this link —
                it was created before the report included{' '}
                {layout.missing.length === 1 ? 'it' : 'them'}. A fresh link will
                have {layout.missing.length === 1 ? 'it' : 'them'}.
              </p>
            ) : null}
            {/* ── 🚨 THE APP'S OWN TABLE, NOT A SECOND ONE ──

                This was a hand-rolled eighteen-column table, and it drifted from
                the Day Overview's on the day it shipped: raw `08:33:34` clocks
                where the app trims them, Net P&L in DOLLARS on a report whose
                every other figure was in points, no `syn` tag, a different
                column ORDER, and no Gross or Commissions. Josh put the two
                side by side and said the tab's is the one he wants.

                ⚠ TWO RENDERERS FOR ONE THING IS THE DRIFT THIS REBUILD EXISTS TO
                END — `TradeTable`'s own header says so, about the same table,
                after the same defect on the Dashboard. Rendering the component
                means the shared copy cannot drift again: it is the same cells,
                the same formatters and the same column list.

                ⚠ NO `columns` PROP, exactly as the Day Overview passes none, so
                the two follow the same default. `shareTableColumns` in
                DayOverview.test asserts the two header rows match.

                ⚠ NO `selection`, so no checkboxes and no sortable headers — the
                read-only shape the component already supports. */}
            <TradeTable
              rows={rows}
              columns={layout.columns}
              kind="annotated"
              /* ⚠ 'calendar', and the dates are ALREADY session-resolved. The
                 sharer's day-boundary rule was applied at capture time; asking a
                 visitor's table to apply it again to a resolved date would move
                 an evening trade a second time. */
              mode="calendar"
              unit={unit}
              valueOf={(t) => valueById.get(String(t.id)) ?? null}
              labelAccount={(t) => accountById.get(String(t.id)) ?? ''}
              labelPattern={(t) => patternById.get(String(t.id)) ?? ''}
              onOpen={(id) => setOpenIndex(indexById.get(String(id)) ?? null)}
            />
          </div>
        </section>
      ) : null}

      {openIndex != null && trades[openIndex] ? (
        <SharedTradeModal
          trade={trades[openIndex]}
          all={trades}
          index={openIndex}
          onNavigate={setOpenIndex}
          onClose={() => setOpenIndex(null)}
        />
      ) : null}
    </div>
  );
}
